import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Delete, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { unlock as unlockAudio } from '../../services/pos/posAudio';

/**
 * POS PIN login screen.
 *
 * • 4-digit numeric PIN entered on an on-screen keypad (POS is a touch
 *   device with no keyboard).
 * • Auto-submits when the 4th digit is entered.
 * • Backend rate-limits: 5 wrong attempts / 60s lockout — this UI surfaces
 *   the 429 response as a countdown.
 */
export default function PosLoginPage() {
  const navigate = useNavigate();
  const { posLogin, token } = useAuth();
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [lockedUntil, setLockedUntil] = useState(0);
  const [countdown, setCountdown] = useState(0);

  // Already signed in? Bounce to the POS shell.
  useEffect(() => {
    if (token) navigate('/pos', { replace: true });
  }, [token, navigate]);

  useEffect(() => {
    if (lockedUntil <= 0) return undefined;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000));
      setCountdown(remaining);
      if (remaining <= 0) { setLockedUntil(0); setError(''); }
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [lockedUntil]);

  const submit = useCallback(async (value) => {
    if (submitting || lockedUntil > 0) return;
    setSubmitting(true);
    setError('');
    try {
      await posLogin(value);
      navigate('/pos', { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || 'Login failed';
      if (status === 429) {
        // Parse "Too many attempts — try again in Ns" for a live countdown.
        const secs = parseInt((detail.match(/(\d+)s/) || [])[1] || '60', 10);
        setLockedUntil(Date.now() + secs * 1000);
      } else if (status === 503) {
        setError('POS PIN not set. Ask your manager to configure it in Admin → Settings.');
      } else {
        setError(detail);
      }
      setPin('');
      // Buzz visual feedback
      toast.error(status === 429 ? 'Locked out — wait and try again' : detail);
    } finally {
      setSubmitting(false);
    }
  }, [submitting, lockedUntil, posLogin, navigate]);

  const pressDigit = (d) => {
    // Every key press is a valid user gesture — use it to unlock the
    // AudioContext so the shell can chime on new orders. Repeat calls
    // are a no-op inside posAudio.
    unlockAudio();
    if (submitting || lockedUntil > 0 || pin.length >= 4) return;
    const next = pin + d;
    setPin(next);
    if (next.length === 4) submit(next);
  };

  const pressBackspace = () => {
    if (submitting || lockedUntil > 0) return;
    setPin((p) => p.slice(0, -1));
    setError('');
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-neutral-900 text-white p-6 select-none"
      data-testid="pos-login-page"
    >
      <div className="w-full max-w-xs">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold mb-1">POS Sign in</h1>
          <p className="text-sm text-neutral-400">Enter the 4-digit PIN</p>
        </div>

        {/* PIN display */}
        <div className="flex gap-3 justify-center mb-6" data-testid="pos-pin-display">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full border-2 ${
                i < pin.length ? 'bg-white border-white' : 'border-neutral-500'
              }`}
            />
          ))}
        </div>

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-3" data-testid="pos-pin-keypad">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => pressDigit(d)}
              disabled={submitting || lockedUntil > 0}
              className="h-16 rounded-xl bg-neutral-800 text-2xl font-semibold active:bg-neutral-700 disabled:opacity-40 transition-colors"
              data-testid={`pos-pin-key-${d}`}
            >
              {d}
            </button>
          ))}
          <div />
          <button
            type="button"
            onClick={() => pressDigit('0')}
            disabled={submitting || lockedUntil > 0}
            className="h-16 rounded-xl bg-neutral-800 text-2xl font-semibold active:bg-neutral-700 disabled:opacity-40 transition-colors"
            data-testid="pos-pin-key-0"
          >
            0
          </button>
          <button
            type="button"
            onClick={pressBackspace}
            disabled={submitting || lockedUntil > 0}
            className="h-16 rounded-xl bg-neutral-800 flex items-center justify-center active:bg-neutral-700 disabled:opacity-40 transition-colors"
            data-testid="pos-pin-key-back"
            aria-label="Backspace"
          >
            <Delete className="h-6 w-6" />
          </button>
        </div>

        {/* Status line */}
        <div className="mt-5 text-center min-h-[24px] text-sm">
          {submitting && (
            <span className="text-neutral-400 inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
            </span>
          )}
          {!submitting && lockedUntil > 0 && (
            <span className="text-amber-400" data-testid="pos-pin-lockout">
              Too many attempts — try again in {countdown}s
            </span>
          )}
          {!submitting && lockedUntil === 0 && error && (
            <span className="text-red-400" data-testid="pos-pin-error">{error}</span>
          )}
        </div>
      </div>
    </div>
  );
}
